import { createFileRoute } from "@tanstack/react-router";
import useRequest from "@/lib/request/useRequest";
import {
    getClassificationLabel,
    Imagery,
    ModelClassification,
} from "@/types/imagery";
import { FieldSummary } from "@/types/field";
import { Key, useEffect, useMemo, useRef, useState } from "react";
import {
    Spinner,
    Selection,
    Button,
    AutocompleteItem,
    Input,
    Divider,
    ButtonGroup,
} from "@heroui/react";
import dayjs, { Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";
import {
    ColumnDef,
    getCoreRowModel,
    getFilteredRowModel,
    getSortedRowModel,
} from "@tanstack/react-table";
import { useReactTable } from "@tanstack/react-table";
import debounce from "lodash.debounce";
import CommonTable from "@/components/table/CommonTable";
import { getColumnValues } from "@/lib/utils/table";
import { faSearch, faThumbsDown } from "@fortawesome/free-solid-svg-icons";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useAuth from "@/lib/auth/useAuth";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { roundTo } from "@/lib/utils/numbers";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";

export const Route = createFileRoute("/dashboard/home/imagery-qaqc")({
    component: RouteComponent,
});

type ImageryRow = Imagery & { field?: FieldSummary };

const useDisplayImage = ({
    imageryId,
    urlPath,
}: {
    imageryId?: Key;
    urlPath: string;
}) => {
    const { axiosRequest } = useRequest();
    const query = useQuery({
        queryKey: ["imagery", imageryId, urlPath],
        queryFn: () =>
            axiosRequest({
                url: `imagery/${imageryId}/${urlPath}/`,
                responseType: "blob",
            }),
        enabled: !!imageryId,
    });
    const [url, setUrl] = useState<string | null>(null);
    useEffect(() => {
        if (!query.data) {
            setUrl(null);
            return;
        }

        const url = URL.createObjectURL(query.data as Blob);
        setUrl(url);

        return () => {
            if (url) {
                URL.revokeObjectURL(url);
            }
        };
    }, [query.data]);

    return { url, isFetching: query.isFetching };
};

function RouteComponent() {
    const { formatDateMMDDYYYY } = useI18n();
    const { t } = useTranslation("messages");
    const { useGetRequest, useMutateRequest } = useRequest();
    const { user } = useAuth();
    const ref = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(550);
    useEffect(() => {
        if (ref.current) {
            setHeight(ref.current.clientHeight);
        }
    }, []);

    const [start_date, setStartDate] = useState<Dayjs>(
        dayjs().subtract(1, "week"),
    );

    const [filterType, setFilterType] = useState<"needs_review" | "usable">(
        "needs_review",
    );

    const flaggedImagery = useGetRequest<Imagery[]>({
        url: "imagery/review/",
        params: {
            start_date: start_date.format("YYYY-MM-DD"),
            filter_type: filterType,
        },
    });

    const sentinelFields = useGetRequest<FieldSummary[]>({
        url: "fields/sentinel_summary/",
    });

    const data: ImageryRow[] = useMemo(() => {
        if (!flaggedImagery.data || !sentinelFields.data) return [];

        return sortArrayByKey(
            flaggedImagery.data.map((imagery) => {
                const field = sentinelFields.data.find(
                    (field) => field.id == imagery.field?.id,
                );
                return {
                    ...imagery,
                    field,
                };
            }),
            "capture_date",
            "desc",
        );
    }, [flaggedImagery.data, sentinelFields.data]);

    const hasImages = useMemo(() => {
        return data.some((imagery) => {
            if (!imagery.field?.service_provider?.account_manager?.full_name) {
                return false;
            }

            return (
                imagery.field?.service_provider?.account_manager?.full_name ==
                user?.full_name
            );
        });
    }, [data, user?.full_name]);

    const [selectedKeys, setSelectedKeys] = useState<Selection>(new Set([]));

    const selectedImageId = useMemo(() => {
        return Array.from([...selectedKeys])[0];
    }, [selectedKeys]);

    const rgbUrl = useDisplayImage({
        imageryId: selectedImageId,
        urlPath: "display_rgb",
    });
    const ndreUrl = useDisplayImage({
        imageryId: selectedImageId,
        urlPath: "display",
    });
    const ndviUrl = useDisplayImage({
        imageryId: selectedImageId,
        urlPath: "display_ndvi",
    });

    const [globalSearch, setGlobalSearch] = useState("");
    const [globalFilter, setGlobalFilter] = useState("");

    useEffect(() => {
        const debouncedUpdate = debounce(() => {
            setGlobalFilter(globalSearch);
        }, 200);

        debouncedUpdate();

        return () => debouncedUpdate.cancel();
    }, [globalSearch]);

    const updateImagery = useMutateRequest({
        successMessage: t("success.imagery_updated"),
        errorMessage: t("error.failed_to_update_imagery"),
    });

    const columns: ColumnDef<ImageryRow>[] = useMemo(
        () => [
            {
                id: "account_manager",
                header: "Account manager",
                accessorFn: (row) =>
                    row.field?.service_provider?.account_manager?.full_name ??
                    "N/A",
            },
            {
                id: "service_provider",
                header: "Service provider",
                accessorFn: (row) => row.field?.service_provider?.name ?? "",
            },
            {
                id: "farm",
                header: "Farm",
                accessorFn: (row) => row.field?.grower?.name ?? "",
            },
            {
                id: "field",
                header: "Field",
                accessorFn: (row) => row.field?.name ?? "",
            },
            {
                id: "capture_date",
                header: "Date",
                sortingFn: "datetime",
                maxSize: 90,
                accessorFn: (row) => row.capture_date,
                cell: (row) => {
                    return (
                        <div className="text-xs flex flex-col">
                            <span>
                                {formatDateMMDDYYYY(row.getValue() as string)}
                            </span>
                            <span>
                                {dayjs(row.getValue() as string).format(
                                    "hh:mm a",
                                )}
                            </span>
                        </div>
                    );
                },
            },
            {
                id: "model_classification",
                header: "Model",
                maxSize: 60,
                accessorFn: (row) => [
                    row.model_classification ?? undefined,
                    row.coverage_percent ?? undefined,
                ],
                cell: (row) => {
                    const [modelClassification, coveragePercent] =
                        row.getValue() as [ModelClassification, number];

                    return (
                        <div className="text-xs flex flex-col items-start">
                            <span
                                className={classNames({
                                    "rounded-md text-white p-[0.15rem] font-bold":
                                        true,
                                    "bg-gray-800": !modelClassification,
                                    "bg-red-500":
                                        modelClassification ==
                                        ModelClassification.bad,
                                    "bg-green-500":
                                        modelClassification ==
                                        ModelClassification.good,
                                    "bg-yellow-500":
                                        modelClassification ==
                                        ModelClassification.flag,
                                })}
                            >
                                {getClassificationLabel(modelClassification) ??
                                    "N/A"}
                            </span>
                            {coveragePercent && (
                                <span className="text-xs">
                                    Cov:
                                    {roundTo(coveragePercent, 0)}%
                                </span>
                            )}
                        </div>
                    );
                },
            },
            {
                id: "si",
                header: "SI",
                maxSize: 80,
                accessorFn: (row) => [row.si_min, row.si_mean],
                cell: (row) => {
                    const [min, mean] = row.getValue() as [number, number];
                    return (
                        <div className="text-xs grid grid-cols-2 ">
                            <span>Min:</span>
                            <span> {roundTo(min, 4)}</span>
                            <span>Mean:</span>
                            <span> {roundTo(mean, 4)}</span>
                        </div>
                    );
                },
            },
            {
                id: "actions",
                header: "Actions",
                accessorFn: () => null,
                maxSize: 90,
                cell: ({ row, table }) => {
                    const index = table
                        .getFilteredRowModel()
                        .rows.findIndex((r) => r.id == row.id);
                    const nextId =
                        index >= 0
                            ? table.getFilteredRowModel().rows[index + 1]
                                ?.original?.id
                            : undefined;

                    return (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                isIconOnly
                                isLoading={updateImagery.isPending}
                                color="success"
                                onPress={() => {
                                    updateImagery.mutate(
                                        {
                                            url: `imagery/${row.original.id}/score/`,
                                            data: {
                                                user_classification:
                                                    ModelClassification.good,
                                            },
                                            method: "put",
                                        },
                                        {
                                            onSuccess: () => {
                                                if (nextId) {
                                                    setSelectedKeys(
                                                        new Set([
                                                            nextId.toString(),
                                                        ]),
                                                    );
                                                }
                                                flaggedImagery.refetch();
                                            },
                                        },
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={faCheck} />
                            </Button>
                            <Button
                                color="danger"
                                isIconOnly
                                size="sm"
                                isLoading={updateImagery.isPending}
                                onPress={() => {
                                    updateImagery.mutate(
                                        {
                                            url: `imagery/${row.original.id}/score/`,
                                            data: {
                                                user_classification:
                                                    ModelClassification.bad,
                                            },
                                            method: "put",
                                        },
                                        {
                                            onSuccess: () => {
                                                if (nextId) {
                                                    setSelectedKeys(
                                                        new Set([
                                                            nextId.toString(),
                                                        ]),
                                                    );
                                                }
                                                flaggedImagery.refetch();
                                            },
                                        },
                                    );
                                }}
                            >
                                <FontAwesomeIcon icon={faThumbsDown} />
                            </Button>
                        </div>
                    );
                },
            },
        ],
        [flaggedImagery, updateImagery, formatDateMMDDYYYY],
    );

    const table = useReactTable<ImageryRow>({
        columns,
        data: data,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            globalFilter,
        },
        initialState: {
            sorting: [{ id: "capture_date", desc: true }],
        },
        onGlobalFilterChange: setGlobalFilter,
        globalFilterFn: (row, columnId, filterValue) => {
            const value = row.getValue(columnId);
            return String(value)
                .toLowerCase()
                .includes(filterValue.toLowerCase());
        },
    });

    useEffect(() => {
        if (hasImages && user?.full_name) {
            const existingFilter = table
                .getState()
                .columnFilters.find((filter) => filter.id == "account_manager");
            if (!existingFilter) {
                table.setColumnFilters([
                    ...table.getState().columnFilters,
                    {
                        id: "account_manager",
                        value: user.full_name,
                    },
                ]);
            }
        }
    }, [hasImages, table, user?.full_name]);

    const { rows: prefilteredRows } = table.getPreFilteredRowModel();

    const filterOptions = {
        account_manager: getColumnValues(
            "account_manager",
            prefilteredRows,
        ).map((account_manager) => {
            return (
                <AutocompleteItem
                    key={account_manager}
                    textValue={account_manager}
                >
                    {account_manager}
                </AutocompleteItem>
            );
        }),

        service_provider: getColumnValues(
            "service_provider",
            prefilteredRows,
        ).map((service_provider) => {
            return (
                <AutocompleteItem
                    key={service_provider}
                    textValue={
                        service_provider != ""
                            ? service_provider
                            : "no service provider"
                    }
                >
                    {service_provider}
                </AutocompleteItem>
            );
        }),

        farm: getColumnValues("farm", prefilteredRows).map((farm) => {
            return (
                <AutocompleteItem
                    key={farm}
                    textValue={farm != "" ? farm : "no farm"}
                >
                    {farm}
                </AutocompleteItem>
            );
        }),
    };

    return (
        <div className="grid grid-rows-[1fr_1fr] md:grid-rows-none md:grid-cols-[2fr_1fr] h-full overflow-hidden ">
            <div className="h-full flex flex-col">
                <div className="flex flex-row gap-2 p-2 items-center justify-between">
                    <div>
                        <Input
                            startContent={<FontAwesomeIcon icon={faSearch} />}
                            value={globalSearch}
                            isClearable
                            onClear={() => setGlobalSearch("")}
                            onChange={(e) =>
                                setGlobalSearch(String(e.target.value))
                            }
                            placeholder="Search..."
                        />
                    </div>
                    <div className="flex flex-row gap-2">
                        <ButtonGroup>
                            <Button
                                color={
                                    filterType == "needs_review"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setFilterType("needs_review")}
                            >
                                Needs Review
                            </Button>
                            <Button
                                color={
                                    filterType == "usable"
                                        ? "primary"
                                        : "default"
                                }
                                onPress={() => setFilterType("usable")}
                            >
                                Usable
                            </Button>
                        </ButtonGroup>
                        <Button
                            onPress={() =>
                                setStartDate(dayjs().subtract(1, "week"))
                            }
                            color={
                                start_date.isSame(
                                    dayjs().subtract(1, "week"),
                                    "day",
                                )
                                    ? "primary"
                                    : "default"
                            }
                        >
                            Last Week
                        </Button>
                        <Button
                            onPress={() =>
                                setStartDate(dayjs().subtract(2, "week"))
                            }
                            color={
                                start_date.isSame(
                                    dayjs().subtract(2, "week"),
                                    "day",
                                )
                                    ? "primary"
                                    : "default"
                            }
                        >
                            Last 2 Weeks
                        </Button>
                        <Button
                            onPress={() =>
                                setStartDate(dayjs().subtract(4, "month"))
                            }
                            color={
                                start_date.isSame(
                                    dayjs().subtract(1, "month"),
                                    "day",
                                )
                                    ? "primary"
                                    : "default"
                            }
                        >
                            Last Month
                        </Button>
                    </div>
                </div>
                <Divider />
                <div
                    ref={ref}
                    className="h-[100%] overflow-y-scroll p-1 scrollbar-hide"
                >
                    <CommonTable
                        table={table}
                        label="Flagged Imagery"
                        height={height}
                        tableProps={{
                            selectedKeys: selectedKeys,
                            color: "primary",
                            selectionMode: "single",
                            onSelectionChange: setSelectedKeys,
                            scrollRef: ref,
                        }}
                        filterOptions={filterOptions}
                        isLoading={
                            flaggedImagery.isFetching ||
                            sentinelFields.isFetching
                        }
                    />
                </div>
            </div>
            <div className="flex flex-row overflow-y-scroll">
                <Divider orientation="vertical" />
                <div className="p-2 flex flex-col gap-2 justify-start items-start  ">
                    <div>Click Imagery To View</div>
                    <div className="flex flex-col gap-2">
                        <p className="font-semibold">RGB</p>
                        {rgbUrl.isFetching ? (
                            <Spinner label="Loading..." />
                        ) : (
                            <>
                                {rgbUrl.url && (
                                    <img
                                        src={rgbUrl.url}
                                        className="max-h-[20rem] w-auto aspect-auto border-2 border-gray-500 rounded-md scale-x-[-1] rotate-180"
                                    />
                                )}
                            </>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <p className="font-semibold">NDRE</p>
                        {ndreUrl.isFetching ? (
                            <Spinner label="Loading..." />
                        ) : (
                            <>
                                {ndreUrl.url && (
                                    <img
                                        src={ndreUrl.url}
                                        className="max-h-[20rem] w-auto   border-2 border-gray-500 rounded-md scale-x-[-1] rotate-180"
                                    />
                                )}
                            </>
                        )}
                    </div>
                    <div className="flex flex-col gap-2">
                        <p className="font-semibold">NDVI</p>
                        {ndviUrl.isFetching ? (
                            <Spinner label="Loading..." />
                        ) : (
                            <>
                                {ndviUrl.url && (
                                    <img
                                        src={ndviUrl.url}
                                        className="max-h-[20rem] w-auto  border-2 border-gray-500 rounded-md scale-x-[-1] rotate-180"
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
